import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Spiner } from "../../../components/spinner/Spiner";
import TableList, { GridCol, GridRow } from "../../../components/table/TableList"
import Button from "../../../components/button/Button"
import companiesQuery from "../../../firestore/Companies.query";
import systemParametersQuery from "../../../firestore/SystemParameters.query";
import { OptionContract, initOptionContract, nowDate } from "../../../model/OptionContract";
import { OptionContractModal } from "./OptionContractModal";
import { OptionContractConfirmModal } from "./OptionContractConfirmModal";
import { convertDateToString } from "../../../utility/ConvertUtily";
import { alertOpen, AlertValue } from "../../../features/alert.slice";
import { ConstMessage, SessionKey } from "../../../config/const";
import { optionConfig } from "../../../model/Systemparameters";
import companiesApi from "../../../api/Companies.api";

export const OptionContractSettingPage = () => {

  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [isOpenConfirm, setIsOpenConfirm] = useState<boolean>(false);
  const [isLoad, setIsLoad] = useState(true);
  const [optionList, setOptionList] = useState<OptionContract[]>([]);
  const [selectOption, setSelectOption] = useState<OptionContract>(initOptionContract()[0]);
  const [confirmData, setConfirmData] = useState<OptionContract>(initOptionContract()[0]);
  const [isUpdate, setIsUpdate] = useState(false);

  // URLパラメータからtenantId取得
  const { tenantId } = useParams<{ tenantId: string }>();

  useEffect(() => {
    const f = async () => {
      try {
        setIsLoad(true);
        // 団体のオプション契約を取得
        const options = await companiesQuery.getOptionContractList(tenantId!);
        // マスタのオプション契約を取得
        const masterOptionData = await systemParametersQuery.getOptionContract();
        let optionResult: OptionContract[] = [];
        const masterOptionKeys = Object.keys(masterOptionData);
        for(let i = 0; i < masterOptionKeys.length; i++){
          const key = masterOptionKeys[i];
          if(masterOptionData[key].status){
            let option: OptionContract;
            option = options.filter((option) => option.optionId === masterOptionData[key].optionId)[0];
            if(option){
              option.sort =  masterOptionData[key].sort;
            }else{
              option = {
                id: "",
                optionId: masterOptionData[key].optionId,
                optionName: masterOptionData[key].optionName,
                status: false,
                applicableStartDate: null,
                applicableFinishDate: null,
                sort: masterOptionData[key].sort,
              }
            }
            optionResult.push(option);
          }
        }
        setOptionList(optionResult);
        setIsLoad(false);
      } catch (err: any) {
        console.error(err);
        setIsLoad(false);
        dispatch(alertOpen(AlertValue(ConstMessage.ERR_GET_DATA, "danger")));
      }
    };
    f();
  }, [isOpen]);

  const statusName = (status: boolean, startDate: Date | string | null, finishDate: Date | string | null) => {
    if(status && (
      (startDate !== null && finishDate !== null && startDate <= nowDate && finishDate >= nowDate && startDate <= finishDate) || 
      (startDate === null && finishDate === null) || 
      (finishDate === null && startDate !== null && startDate <= nowDate) || 
      (startDate === null && finishDate !== null && finishDate >= nowDate)))
    {
      return <span className={"text-green-700 font-bold"}>有効</span>;
    }else if(status && startDate !== null && startDate > nowDate){
      return <span className={"text-yellow-400 font-bold"}>有効（開始待ち）</span>;
    }
    return <span className={"text-red-500 font-bold"}>無効</span>;
  }

  const columns: GridCol[] = [
    {
      field: "optionName",
      headerName: "オプション名",
      style: "w-3/12",
    },
    {
      field: "status",
      headerName: "契約状況",
      style: "w-2/12",
    },
    {
      field: "applicableStartDate",
      headerName: "開始日",
      style: "w-2/12",
    },
    {
      field: "applicableFinishDate",
      headerName: "終了日",
      style: "w-2/12",
    },
    {
      field: "control",
      headerName: "",
      style: "w-1/12",
      sort: false
    },
  ];

  const rowList: GridRow[] = optionList.map<GridRow>((item, index) => {
    return {
      rowData: {
        optionName: item.optionName,
        status: statusName(item.status, item.applicableStartDate, item.applicableFinishDate),
        applicableStartDate: (item.applicableStartDate !== null && typeof item.applicableStartDate !== "string") ? convertDateToString(item.applicableStartDate) : "",
        applicableFinishDate: (item.applicableFinishDate !== null && typeof item.applicableFinishDate !== "string") ? convertDateToString(item.applicableFinishDate) : "",
        control: (
          <div className="flex">
            <Button
              className="mr-2"
              onClick={() => {
                setIsUpdate(true);
                setSelectOption(item);
                toggle();
              }}
              children={"編集"}/>
          </div>
        )
      }
    }
  });

  // オプション契約編集確認画面
  const onClickConfirm = async (data: OptionContract) => {
    setConfirmData(data);
    toggleConfirm();
  };

  const toggle = () => {
    setIsOpen(!isOpen);
  };

  const toggleConfirm = () => {
    setIsOpenConfirm(!isOpenConfirm);
  };

  // オプション契約設定の登録
  const onClickSubmit = async () => {
    try{
      const startDate = new Date(confirmData.applicableStartDate ?? "");
      const finishDate = new Date(confirmData.applicableFinishDate ?? "");
      const startDateResult = confirmData.applicableStartDate !== null && !Number.isNaN(startDate.getTime()) ? confirmData.applicableStartDate : null;
      const finishDateResult = confirmData.applicableFinishDate !== null && !Number.isNaN(finishDate.getTime()) ? confirmData.applicableFinishDate : null;
      const value: OptionContract = {
        id: confirmData.id,
        optionId: confirmData.optionId,
        optionName: confirmData.optionName,
        status: confirmData.status,
        applicableStartDate: startDateResult,
        applicableFinishDate: finishDateResult,
      }
      if(confirmData.id !== "" && confirmData.id !== undefined){
        await companiesQuery.updateOptionContract(tenantId!, value);
      }else{
        await companiesQuery.setOptionContract(tenantId!, value);
      }
    }catch(err: any){
      dispatch(alertOpen(AlertValue("オプション契約設定の更新に失敗しました。", "danger")));
    }
    toggle();
    toggleConfirm();
    // 利用者IDカードの権限の有無を取得
    const userIdCardAuth = await companiesApi.requestIdCardAuth(tenantId!);
    sessionStorage.setItem(SessionKey.USER_ID_CARD_AUTH, String(userIdCardAuth.success ? userIdCardAuth.data : false));
    window.location.reload();
  };

  return(
    <div className="h-full">
      {(isOpen) && (<OptionContractModal
        children={undefined}
        open={isOpen}
        toggle={toggle}
        closeOnClickOutside={false}
        updateFlg={isUpdate}
        selectOption={selectOption}
        onClickConfirm={onClickConfirm}
      />)}
      <OptionContractConfirmModal
        children={undefined}
        open={isOpenConfirm}
        toggle={toggleConfirm}
        closeOnClickOutside={false}
        data={confirmData}
        onClickSubmit={onClickSubmit}
      />
      <div className="flex items-center space-x-4 p-6 justify-between">
        <h2 className="flex text-xl font-semibold text-gray-900">オプション契約設定</h2>
      </div>
      {isLoad === true ? <Spiner /> :(
        <div className={"px-6"}>
          <TableList
            header={false}
            footer={false}
            defaultSortKey={"sort"}
            sort={false}
            gridCol={columns}
            gridRow={rowList}
          />
        </div>
      )}
    </div>
  );
}